import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Video, Users, ChevronRight, Award, Book, Calendar, Clock, DollarSign, Briefcase, GraduationCap, Brain, Dumbbell, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';

const courseModules = [
  {
    title: 'Anatomy & Physiology',
    icon: Brain,
    duration: '60 hours',
    topics: [
      'Musculoskeletal system',
      'Cardiovascular system',
      'Respiratory system',
      'Energy systems',
      'Movement mechanics'
    ]
  },
  {
    title: 'Exercise Science',
    icon: Dumbbell,
    duration: '80 hours',
    topics: [
      'Program design principles',
      'Exercise selection & progression',
      'Training methodologies',
      'Recovery strategies',
      'Performance assessment'
    ]
  },
  {
    title: 'Nutrition Fundamentals',
    icon: Heart,
    duration: '40 hours',
    topics: [
      'Macronutrients & micronutrients',
      'Meal planning basics',
      'Nutrition for performance',
      'Supplementation guidelines',
      'Hydration strategies'
    ]
  },
  {
    title: 'Business Skills',
    icon: Briefcase,
    duration: '40 hours',
    topics: [
      'Client acquisition',
      'Marketing strategies',
      'Business operations',
      'Financial management',
      'Legal considerations'
    ]
  }
];

const careerPaths = [
  {
    title: 'Personal Trainer',
    salary: '$50,000 - $100,000+',
    description: 'Work one-on-one with clients to achieve their fitness goals',
    color: 'from-blue-500/20 to-cyan-500/20'
  },
  {
    title: 'Group Fitness Instructor',
    salary: '$45,000 - $80,000',
    description: 'Lead engaging group fitness classes and bootcamps',
    color: 'from-cyan-500/20 to-teal-500/20'
  },
  {
    title: 'Online Coach',
    salary: '$40,000 - $120,000+',
    description: 'Provide remote coaching and programming services',
    color: 'from-teal-500/20 to-emerald-500/20'
  },
  {
    title: 'Corporate Wellness',
    salary: '$55,000 - $90,000',
    description: 'Design and implement workplace wellness programs',
    color: 'from-emerald-500/20 to-blue-500/20'
  }
];

const courseDetails = [
  {
    icon: Calendar,
    title: 'Duration',
    details: [
      '6-12 months flexible completion',
      'Part-time study options',
      'Self-paced learning'
    ]
  },
  {
    icon: DollarSign,
    title: 'Investment',
    details: [
      '$3,000 total course fee',
      'Payment plans available',
      'Student loan eligible'
    ]
  },
  {
    icon: Book,
    title: 'Study Format',
    details: [
      'Blended learning approach',
      'Online theory modules',
      'In-person practical sessions'
    ]
  },
  {
    icon: Award,
    title: 'Certification',
    details: [
      'Level 4 PT Certification',
      'Internationally recognized',
      'REPs registered qualification'
    ]
  }
];

export default function PTCoursePage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://i.ibb.co/rGbPGjfq/DSC01317.jpg')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h1 className="text-7xl font-bold mb-4">BECOME A PERSONAL TRAINER</h1>
              <p className="text-xl text-gray-400">Level 4 Personal Training Certification</p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="mt-8"
              >
                <a
                  href="#contact"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300"
                >
                  <span>START YOUR JOURNEY</span>
                  <ChevronRight className="w-5 h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Course Overview */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="prose prose-invert max-w-none mb-16"
            >
              <p className="text-xl text-gray-400">
                Transform your passion for fitness into a rewarding career with our comprehensive Personal Training Certification. This industry-leading qualification combines cutting-edge exercise science with practical business skills, preparing you for success in the dynamic fitness industry.
              </p>
            </motion.div>

            {/* Course Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
              {courseDetails.map((detail, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/10"
                >
                  <detail.icon className="w-8 h-8 text-blue-400 mb-4" />
                  <h3 className="text-xl font-bold mb-4">{detail.title}</h3>
                  <ul className="space-y-2">
                    {detail.details.map((item, i) => (
                      <li key={i} className="text-gray-400">{item}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Course Modules */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <h2 className="text-4xl font-bold mb-12 text-center">COURSE MODULES</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {courseModules.map((module, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.8 }}
                    className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/10"
                  >
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="bg-blue-500/20 p-3 rounded-full">
                        <module.icon className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{module.title}</h3>
                        <p className="text-gray-400">{module.duration}</p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {module.topics.map((topic, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span className="text-gray-300">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Career Opportunities */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-20"
            >
              <h2 className="text-4xl font-bold mb-12 text-center">CAREER OPPORTUNITIES</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {careerPaths.map((career, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.8 }}
                    className={`bg-gradient-to-br ${career.color} backdrop-blur-sm rounded-2xl p-6 border border-blue-500/10`}
                  >
                    <h3 className="text-xl font-bold mb-2">{career.title}</h3>
                    <p className="text-blue-400 font-bold mb-4">{career.salary}</p>
                    <p className="text-gray-300">{career.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Study Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-1 gap-8 mb-20"
            >
              <div className="space-y-6">
                <h2 className="text-4xl font-bold mb-6">COMPREHENSIVE SUPPORT</h2>
                <p className="text-gray-400 mb-8">
                  Our dedicated support team ensures you have everything you need to succeed in your studies and launch your fitness career.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-4 bg-white/5 rounded-xl p-6">
                    <Book className="w-6 h-6 text-blue-400" />
                    <div>
                      <h4 className="font-bold">Study Materials</h4>
                      <p className="text-sm text-gray-400">Comprehensive digital resources and textbooks</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 bg-white/5 rounded-xl p-6">
                    <Users className="w-6 h-6 text-cyan-400" />
                    <div>
                      <h4 className="font-bold">Practical Workshops</h4>
                      <p className="text-sm text-gray-400">Hands-on experience with real clients</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 bg-white/5 rounded-xl p-6">
                    <Video className="w-6 h-6 text-teal-400" />
                    <div>
                      <h4 className="font-bold">Online Learning</h4>
                      <p className="text-sm text-gray-400">Interactive video content and assessments</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Enrollment Process */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <h2 className="text-4xl font-bold mb-12">ENROLLMENT PROCESS</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl p-6 border border-blue-500/10">
                  <div className="text-4xl font-bold text-blue-400 mb-4">01</div>
                  <h3 className="text-xl font-bold mb-2">Initial Consultation</h3>
                  <p className="text-gray-400">Meet with our course advisor to discuss your career goals</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-2xl p-6 border border-cyan-500/10">
                  <div className="text-4xl font-bold text-cyan-400 mb-4">02</div>
                  <h3 className="text-xl font-bold mb-2">Enrollment</h3>
                  <p className="text-gray-400">Complete enrollment forms and arrange payment plan</p>
                </div>
                <div className="bg-gradient-to-br from-teal-500/10 to-emerald-500/10 rounded-2xl p-6 border border-teal-500/10">
                  <div className="text-4xl font-bold text-teal-400 mb-4">03</div>
                  <h3 className="text-xl font-bold mb-2">Begin Your Journey</h3>
                  <p className="text-gray-400">Access course materials and start your learning journey</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}